import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  ExerciseType,
  ExerciseIntensity,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  ModalBody,
} from "@tandemdiabetes/sugarmate-react-components"

import { DurationInput } from "../DurationInput"
import { useEntryModalPrivateContext } from "../EntryModalPrivateContext"
import { SubEntryFooter } from "../SubEntryFooter"
import { SubEntryHeader } from "../SubEntryHeader"
import { useUpdateActivity } from "../useUpdateActivity"

import { ExerciseIntensityRadioButton } from "./ExerciseIntensityRadioButton"
import { ExerciseTypeSelect } from "./ExerciseTypeSelect"
import { usePolyglot } from "src/contexts"
import styles from "src/styles/entryModal.module.scss"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

interface Props {
  currentSubEntry: EditableSubEntry<ActivityType.Exercise>;
  updateSubEntry: (s: Partial<EditableSubEntry<ActivityType.Exercise>>) => void;
  currentUserEntry: EditableUserEntry;
  setCurrentUserEntry: (userEntry: EditableUserEntry) => void;
}

const max = 480
const sliderStep = 10
const polyglotPrefix = "components.entry_modal.exercise."

export function ExerciseForm(props: Props) {
  const {
    currentSubEntry,
    updateSubEntry,
    currentUserEntry,
    setCurrentUserEntry,
  } = props

  const { exercise: activity } = currentSubEntry

  const updateActivity = useUpdateActivity(
    ActivityType.Exercise,
    activity,
    updateSubEntry,
  )

  const polyglot = usePolyglot()
  const { readOnly } = useEntryModalPrivateContext() ?? false

  const exerciseTypeValue = activity?.exercise_type ?? ExerciseType.None
  const exerciseIntensityValue = activity?.intensity ?? ExerciseIntensity.None

  return (
    <>
      <SubEntryHeader
        activityType={ActivityType.Exercise}
        currentSubEntry={currentSubEntry}
        currentUserEntry={currentUserEntry}
        setCurrentUserEntry={setCurrentUserEntry}
        readOnly={readOnly}
        closeBtnId="exercise-entry-close-btn"
      />
      <ModalBody className={styles.modalBody}>
        <DurationInput
          currentSubEntry={currentSubEntry}
          updateSubEntry={updateSubEntry}
          max={max}
          sliderStep={sliderStep}
          readOnly={readOnly}
        />
        {readOnly ? (
          exerciseTypeValue !== ExerciseType.None && (
            <Label>
              {polyglot.t(`exercise_type.${exerciseTypeValue}`)}
            </Label>
          )
        ) : (
          <ExerciseTypeSelect
            activity={activity}
            updateActivity={updateActivity}
          />
        )}
        {readOnly ? (
          exerciseIntensityValue !== ExerciseIntensity.None ? (
            <Label>
              {polyglot.t(`${polyglotPrefix}intensity.legend_with_colon`)}
              {" "}
              {polyglot.t(`${polyglotPrefix}intensity.${exerciseIntensityValue}`)}
            </Label>
          ) : null
        ) : (
          <ExerciseIntensityRadioButton
            activity={activity}
            updateActivity={updateActivity}
          />
        )}
        <SubEntryFooter
          currentSubEntry={currentSubEntry}
          currentUserEntry={currentUserEntry}
          updateSubEntry={updateSubEntry as (s: Partial<EditableSubEntry<SubEntryType>>) => void}
          readOnly={readOnly}
          allowCreateWithoutChanges
        />
      </ModalBody>
    </>
  )
}
